/**
 * The Company portal this page is on (ADR-024 §4.7), from the host profile.
 * Only ever fetched on a portal; on fias.io it is disabled and returns nothing.
 */
import { useQuery } from '@tanstack/react-query';
import { api, type HostProfileDto } from '@/lib/api';
import { IS_COMPANY_PORTAL } from '@/lib/portal-host';
import { queryKeys } from '@/lib/query-keys';
import { archePath } from '@/lib/arche-url';

export type PortalProfile = Extract<HostProfileDto, { kind: 'company' }>;

export function usePortalProfile() {
  const query = useQuery({
    queryKey: queryKeys.companyPortals.hostProfile(),
    queryFn: () => api.getHostProfile(),
    enabled: IS_COMPANY_PORTAL,
    staleTime: 60_000,
    retry: 1,
  });
  const profile = query.data?.kind === 'company' ? query.data : null;
  return { profile, isLoading: query.isLoading, isError: query.isError, refetch: query.refetch };
}

/** Who the portal belongs to, as its people know it — the Company first. */
export function portalName(profile: PortalProfile): string {
  return profile.brand?.displayName || profile.companyName || profile.displayLabel || 'Portal';
}

/**
 * The address the portal's landing arche is SHOWN at — `/a/<slug>` for a
 * plugin arche with a slug, `/a/<id>` otherwise (`archePath`, the same rule as
 * the route guard), so the portal goes there directly instead of through the
 * guard's `arc_` → slug redirect.
 */
export function portalLandingPath(landing: NonNullable<PortalProfile['landing']>): string {
  return `/a/${archePath({ archId: landing.archeId, slug: landing.slug })}`;
}

/**
 * Whether the arche at URL segment `segment` should load without the
 * platform's chrome: on a portal, when it is the portal's full-screen landing
 * — or while the profile that says so is still loading, so the header and
 * sidebar never flash before a full-screen landing. Always false on fias.io.
 *
 * The segment may be the landing's id OR its slug: a plugin arche is shown at
 * `/a/<slug>` (the route guard canonicalizes a raw `arc_` URL onto it), and a
 * check against the id alone went false at exactly the moment the canonical
 * page started loading — flashing the chrome in.
 */
export function useChromelessPortalLanding(segment: string): boolean {
  const { profile, isLoading } = usePortalProfile();
  if (!IS_COMPANY_PORTAL) return false;
  if (isLoading) return true;
  const landing = profile?.landing;
  if (!landing?.fullScreen) return false;
  return segment === landing.archeId || (!!landing.slug && segment === landing.slug);
}
